import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { addNewPatient } from "../../../api/abhaApi";
import {
  getDepartments,
  getDoctors,
  checkExistingPatient,
  updatePatientAndVisit,
  api,
} from "../../../api/apiService";
import {
  DEFAULT_INDIAN_INSURANCE_PROVIDERS,
  DEFAULT_INDIAN_ADMISSION_REASONS,
} from "../../../constants/patientDefaults";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import "../../../assets/scss/abha-modal.css";
import { getGender } from "./utils";
import {
  sanitizeProfileFields,
  validatePatientField,
  deepSanitizePayload,
} from "../../../utils/inputValidation";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";

const Patients = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { canAny, user } = useAuth();
  const canWritePatient = canAny(
    PERMISSIONS.PATIENTS_REGISTER,
    PERMISSIONS.PATIENTS_UPDATE
  );

  // Patient form state
  const [patientData, setPatientData] = useState({
    fullName: "",
    age: "",
    gender: "",
    mobileNumber: "",
    aadharNumber: "",
    uhid: "",
    bloodGroup: "",
    dob: "",
    // Additional fields from profile API
    address: "",
    pincode: "",
    state: "",
    district: "",
    email: "",
    emergencyContact: "",
    policyNumber: "",
    abhaAddress: "",
    abhaNumber: "",
    existingMedicalConditions: "",
    allergies: "",
    ongoingMedications: "",
  });
  const [isAbhaLinked, setIsAbhaLinked] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false); // Add loading state for register button
  const [formFieldErrors, setFormFieldErrors] = useState({});
  const [lockedFields, setLockedFields] = useState({
    fullName: false,
    age: false,
    gender: false,
    mobileNumber: false,
    aadharNumber: false,
    uhid: false,
    address: false,
    pincode: false,
    state: false,
    district: false,
    email: false,
    emergencyContact: false,
    abhaAddress: false,
    abhaNumber: false,
  });

  // State for API data
  const [allDoctors, setAllDoctors] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState(
    DEFAULT_INDIAN_INSURANCE_PROVIDERS,
  );
  const [admissionReasons, setAdmissionReasons] = useState(
    DEFAULT_INDIAN_ADMISSION_REASONS,
  );
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [consultationFee, setConsultationFee] = useState("");
  const [selectedInsuranceProvider, setSelectedInsuranceProvider] =
    useState("");
  const [selectedAdmissionReason, setSelectedAdmissionReason] = useState("");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
  const [initialDepositAmount, setInitialDepositAmount] = useState("");
  const [visitType, setVisitType] = useState("General Visit");
  const [visitDescription, setVisitDescription] = useState("");
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Check existing flow state
  const [existingPatients, setExistingPatients] = useState([]);
  const [showExistingBanner, setShowExistingBanner] = useState(false);
  const [selectedExistingPatient, setSelectedExistingPatient] = useState(null);
  const [showPatientSelector, setShowPatientSelector] = useState(false);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);
  const [registrationMode, setRegistrationMode] = useState("new");

  // Handle browser back button
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show confirmation if form has data
      const hasData =
        patientData.fullName || patientData.mobileNumber || patientData.age;
      if (hasData) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };

    const handlePopState = () => {
      // Navigate to patients page when back button is pressed
      navigate("/patients");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, patientData]);

  // Helper to extract list from varied API envelope formats
  const extractList = (res, key) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.[key])) return res[key];
    if (Array.isArray(res?.data?.[key])) return res.data[key];
    return [];
  };

  // API service functions
  const fetchDoctors = async (deptId = "") => {
    try {
      const data = await getDoctors(deptId);
      const doctorList = extractList(data, "doctors");
      const list = Array.isArray(doctorList) ? doctorList : [];
      if (!deptId) {
        setAllDoctors(list);
        setDoctors(list);
      } else {
        setDoctors(list);
      }
      return list;
    } catch (error) {
      console.error("Error fetching doctors:", error);
      if (!deptId && allDoctors.length === 0) {
        showError("Error loading doctors");
      }
      return [];
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await getDepartments();
      const deptList = extractList(data, "departments");
      const list = Array.isArray(deptList) ? deptList : [];
      setDepartments(list);
      return list;
    } catch (error) {
      console.error("Error fetching departments:", error);
      showError("Error loading departments");
      return [];
    }
  };

  const fetchInsuranceProviders = async () => {
    try {
      const response = await api.get("/insurance-providers");
      const list = extractList(
        response?.data ?? response,
        "insuranceProviders",
      );
      if (Array.isArray(list) && list.length > 0) {
        setInsuranceProviders(list);
        return list;
      }
    } catch (error) {
      // Endpoint not yet in backend; gracefully use default Indian insurers without error toast
      console.warn(
        "Insurance providers endpoint not available, using default Indian insurers",
      );
    }
    setInsuranceProviders(DEFAULT_INDIAN_INSURANCE_PROVIDERS);
    return DEFAULT_INDIAN_INSURANCE_PROVIDERS;
  };

  const fetchAdmissionReasons = async () => {
    try {
      const response = await api.get("/admission-reasons");
      const list = extractList(
        response?.data ?? response,
        "admissionReasons",
      );
      if (Array.isArray(list) && list.length > 0) {
        setAdmissionReasons(list);
        return list;
      }
    } catch (error) {
      // Endpoint not yet in backend; gracefully use default Indian admission reasons without error toast
      console.warn(
        "Admission reasons endpoint not available, using default Indian admission reasons",
      );
    }
    setAdmissionReasons(DEFAULT_INDIAN_ADMISSION_REASONS);
    return DEFAULT_INDIAN_ADMISSION_REASONS;
  };

  // Fetch all required data on component mount
  useEffect(() => {
    let isMounted = true;
    const fetchAllData = async () => {
      setIsLoadingData(true);
      try {
        await Promise.allSettled([
          fetchDepartments(),
          fetchDoctors(),
          fetchInsuranceProviders(),
          fetchAdmissionReasons(),
        ]);
      } catch (error) {
        console.error("Error fetching form data:", error);
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
        }
      }
    };

    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Auto-fill from ABHA profile if returning from /abha-home via Continue button
  useEffect(() => {
    const stored = localStorage.getItem("abhaProfileForPatient");
    if (stored) {
      try {
        const profile = JSON.parse(stored);
        handleProfileSelected(profile);
      } catch (e) {
        console.error("Failed to parse abhaProfileForPatient", e);
      }
      localStorage.removeItem("abhaProfileForPatient");
    }
  }, []);

  // Handle doctor selection and auto-populate consultation fee and department
  const handleDoctorChange = (doctorId) => {
    setSelectedDoctor(doctorId);
    const selectedDoctorData =
      allDoctors.find((doctor) => doctor._id === doctorId) ||
      doctors.find((doctor) => doctor._id === doctorId);

    if (selectedDoctorData) {
      if (
        selectedDoctorData.consultationFee !== undefined &&
        selectedDoctorData.consultationFee !== null
      ) {
        setConsultationFee(selectedDoctorData.consultationFee.toString());
      }
      const autoDeptId =
        selectedDoctorData.department?._id ||
        selectedDoctorData.departmentId ||
        (typeof selectedDoctorData.department === "string"
          ? selectedDoctorData.department
          : "");

      if (autoDeptId) {
        setSelectedDepartment(autoDeptId);
      }
    } else {
      setConsultationFee("");
    }
  };

  // Handle department selection and filter doctors accordingly
  const handleDepartmentChange = (deptId) => {
    setSelectedDepartment(deptId);

    if (!deptId) {
      // If cleared, display all loaded doctors
      setDoctors(allDoctors);
      return;
    }

    // Filter doctors for selected department
    const filtered = allDoctors.filter((doc) => {
      const docDeptId =
        doc.department?._id ||
        doc.departmentId ||
        (typeof doc.department === "string" ? doc.department : "");
      const docDeptName = doc.department?.name || doc.specialization;
      const selectedDeptObj = departments.find((d) => d._id === deptId);
      return (
        docDeptId === deptId ||
        (selectedDeptObj &&
          docDeptName &&
          docDeptName.toLowerCase() === selectedDeptObj.name?.toLowerCase())
      );
    });

    if (filtered.length > 0) {
      setDoctors(filtered);
    } else {
      // Query backend for this department if not found locally
      fetchDoctors(deptId);
    }

    // If currently selected doctor doesn't belong to newly selected department, clear doctor
    if (selectedDoctor) {
      const curDoc =
        allDoctors.find((d) => d._id === selectedDoctor) ||
        doctors.find((d) => d._id === selectedDoctor);
      if (curDoc) {
        const curDocDeptId =
          curDoc.department?._id ||
          curDoc.departmentId ||
          (typeof curDoc.department === "string" ? curDoc.department : "");
        const curDocDeptName = curDoc.department?.name || curDoc.specialization;
        const selectedDeptObj = departments.find((d) => d._id === deptId);
        const matches =
          curDocDeptId === deptId ||
          (selectedDeptObj &&
            curDocDeptName &&
            curDocDeptName.toLowerCase() ===
              selectedDeptObj.name?.toLowerCase());

        if (!matches) {
          setSelectedDoctor("");
          setConsultationFee("");
        }
      }
    }
  };

  // Handle successful ABHA profile selection
  const handleProfileSelected = (selectedProfile) => {
    // Enhanced field mapping with all available profile API response fields
    const getName = () => {
      if (selectedProfile.name && typeof selectedProfile.name === "string")
        return selectedProfile.name;
      if (selectedProfile.firstName && selectedProfile.lastName) {
        return `${selectedProfile.firstName} ${selectedProfile.middleName ? selectedProfile.middleName + ' ' : ''}${selectedProfile.lastName}`;
      }
      if (selectedProfile.firstName && typeof selectedProfile.firstName === "string")
        return selectedProfile.firstName;
      if (selectedProfile.f_name && typeof selectedProfile.f_name === "string") {
        return `${selectedProfile.f_name} ${selectedProfile.m_name ? selectedProfile.m_name + ' ' : ''}${selectedProfile.l_name}`;
      }
      return selectedProfile.fullName || "";
    };

    const getAge = () => {
      // Calculate age from profile API response date components
      if (selectedProfile.age) return selectedProfile.age.toString();

      // Use yearOfBirth, monthOfBirth, dayOfBirth from profile API
      if (selectedProfile.yearOfBirth) {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
        const currentDay = currentDate.getDate();

        let age = currentYear - parseInt(selectedProfile.yearOfBirth);

        // Adjust age based on birth month and day if available
        if (selectedProfile.monthOfBirth && selectedProfile.dayOfBirth) {
          const birthMonth = parseInt(selectedProfile.monthOfBirth);
          const birthDay = parseInt(selectedProfile.dayOfBirth);

          // If birthday hasn't occurred this year yet, subtract 1
          if (
            currentMonth < birthMonth ||
            (currentMonth === birthMonth && currentDay < birthDay)
          ) {
            age--;
          }
        }

        return age.toString();
      }

      // Fallback to dateOfBirth if available
      if (selectedProfile.dateOfBirth) {
        const birthYear = new Date(selectedProfile.dateOfBirth).getFullYear();
        const currentYear = new Date().getFullYear();
        return (currentYear - birthYear).toString();
      }

      return ""; // Return empty if no age data available
    };

    const getMobile = () => {
      // Get mobile from profile API response
      return (
        selectedProfile.mobile ||
        selectedProfile.mobileNumber ||
        selectedProfile.phoneNumber ||
        selectedProfile.phone ||
        ""
      );
    };

    const getGender = () => {
      // Map gender from profile API response
      const gender = selectedProfile.gender || selectedProfile.sex || "";
      if (gender === "M" || gender === "Male" || gender === "MALE")
        return "Male";
      if (gender === "F" || gender === "Female" || gender === "FEMALE")
        return "Female";
      if (gender === "O" || gender === "Other" || gender === "OTHER")
        return "Other";
      return "";
    };

    const getAbhaNumber = () => {
      // Get ABHA number from profile API response
      let abhaNum =
        selectedProfile.ABHANumber ||
        selectedProfile.abhaNumber ||
        selectedProfile.aadharNumber ||
        selectedProfile.aadhaarNumber ||
        "";

      // Format ABHA number with dashes if not already formatted
      if (abhaNum && !abhaNum.includes("-") && abhaNum.length >= 12) {
        abhaNum = abhaNum.replace(
          /(\d{2})(\d{4})(\d{4})(\d{4})/,
          "$1-$2-$3-$4",
        );
      }

      return abhaNum;
    };

    const getUhid = () => {
      // Get UHID from profile or generate one
      return selectedProfile.uhid || selectedProfile.UHID || "";
    };

    const getAddress = () => {
      // Get full address from profile API response
      return (
        selectedProfile.address ||
        selectedProfile.fullAddress ||
        selectedProfile.permanentAddress ||
        selectedProfile.currentAddress ||
        ""
      );
    };

    const getPincode = () => {
      // Get pincode from profile API response
      return (
        selectedProfile.pincode ||
        selectedProfile.zipCode ||
        selectedProfile.postalCode ||
        ""
      );
    };

    const getState = () => {
      // Get state from profile API response
      return selectedProfile.stateName || selectedProfile.state || "";
    };

    const getDistrict = () => {
      // Get district from profile API response
      return selectedProfile.districtName || selectedProfile.district || "";
    };

    const getEmail = () => {
      // Get email from profile API response
      return selectedProfile.email || selectedProfile.emailAddress || "";
    };

    const getEmergencyContact = () => {
      // Get emergency contact from profile API response
      return (
        selectedProfile.emergencyContact ||
        selectedProfile.emergencyContactNumber ||
        selectedProfile.alternatePhone ||
        ""
      );
    };

    // Build dob (YYYY-MM-DD) from profile's yearOfBirth, monthOfBirth, dayOfBirth
    const getDob = () => {
      const dobRaw =
        selectedProfile.dob ||
        selectedProfile.dateOfBirth ||
        selectedProfile.date_of_birth;
      if (dobRaw) {
        let dobStr = String(dobRaw).split("T")[0];
        const dmyMatch = dobStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmyMatch) {
          return `${dmyMatch[3]}-${dmyMatch[2].padStart(2, "0")}-${dmyMatch[1].padStart(2, "0")}`;
        }
        const ymdMatch = dobStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
        if (ymdMatch) {
          return `${ymdMatch[1]}-${ymdMatch[2].padStart(2, "0")}-${ymdMatch[3].padStart(2, "0")}`;
        }
        return dobStr;
      }
      const y = selectedProfile.yearOfBirth;
      const m = selectedProfile.monthOfBirth;
      const d = selectedProfile.dayOfBirth;
      if (!y) return "";
      const year = String(y).padStart(4, "0");
      const month = m ? String(m).padStart(2, "0") : "01";
      const day = d ? String(d).padStart(2, "0") : "01";
      return `${year}-${month}-${day}`;
    };

    // Auto-fill patient data with comprehensive field mapping
    const newPatientData = {
      fullName: getName(),
      age: getAge(),
      gender: getGender(),
      mobileNumber: getMobile(),
      aadharNumber:
        selectedProfile.aadhaarNumber ||
        selectedProfile.aadharNumber ||
        selectedProfile.aadhar_number ||
        "",
      uhid: getUhid(),
      address: getAddress(),
      pincode: getPincode(),
      state: getState(),
      district: getDistrict(),
      email: getEmail(),
      emergencyContact: getEmergencyContact(),
      abhaAddress:
        selectedProfile.abhaAddress ||
        selectedProfile.abha_address ||
        selectedProfile.abhaaddress ||
        selectedProfile.preferredAbhaAddress ||
        "",
      abhaNumber: getAbhaNumber(),
      dob: getDob(),
    };

    // Reject any non-string/non-number field before it ever reaches form state
    const safePatientData = sanitizeProfileFields(newPatientData);

    // Log which fields failed to auto-fill
    const emptyFields = Object.entries(safePatientData)
      .filter(([key, value]) => !value || value === "")
      .map(([key]) => key);

    if (emptyFields.length > 0) {
      console.warn("Fields that couldn't be auto-filled:", emptyFields);
    }

    setPatientData(safePatientData);

    // Set which specific fields are readonly based on available data
    // Allow editing of age, mobile number, and UHID for user correction
    const abhaLinkedFields = {
      fullName: !!safePatientData.fullName,
      age: false, // Always allow age editing for user verification
      gender: !!safePatientData.gender,
      mobileNumber: false, // Always allow mobile number editing
      aadharNumber: !!safePatientData.aadharNumber,
      uhid: false, // Always allow UHID editing
      address: !!safePatientData.address,
      pincode: !!safePatientData.pincode,
      state: !!safePatientData.state,
      district: !!safePatientData.district,
      email: !!safePatientData.email,
      emergencyContact: false, // Always allow emergency contact editing
      abhaAddress: !!safePatientData.abhaAddress,
      abhaNumber: !!safePatientData.abhaNumber,
    };

    setIsAbhaLinked(true);
    setLockedFields(abhaLinkedFields);

    // Trigger check existing patient right after ABHA info is loaded
    handleCheckExisting(safePatientData);

    // Show success message with details
  };

  // Function to clear all patient data and reset ABHA states
  const handleClearAll = () => {
    // Reset patient data to initial empty state
    setPatientData({
      fullName: "",
      age: "",
      gender: "",
      mobileNumber: "",
      aadharNumber: "",
      uhid: "",
      dob: "",
      address: "",
      pincode: "",
      state: "",
      district: "",
      email: "",
      emergencyContact: "",
      abhaAddress: "",
      abhaNumber: "",
    });

    // Reset ABHA states
    setIsAbhaLinked(false);
    localStorage.removeItem("abhaProfileForPatient");

    // Reset dropdown selections
    setSelectedDoctor("");
    setSelectedDepartment("");
    setConsultationFee("");
    setSelectedInsuranceProvider("");
    setSelectedAdmissionReason("");
    setSelectedPaymentMode("");
    setVisitType("General Visit");
    setVisitDescription("");
    setConsentAgreed(false);
    setDoctors(allDoctors);

    // Reset locked fields
    setLockedFields({
      fullName: false,
      age: false,
      gender: false,
      mobileNumber: false,
      aadharNumber: false,
      uhid: false,
      address: false,
      pincode: false,
      state: false,
      district: false,
      email: false,
      emergencyContact: false,
      abhaAddress: false,
      abhaNumber: false,
    });

    // Clear the temporary ABHA profile handoff if it exists
    sessionStorage.removeItem("selectedAbhaProfile");

    // Clear all unmanaged form fields using DOM manipulation
    setTimeout(() => {
      // Get all form elements within the patient page
      const patientPageElement = document.querySelector(".patient-page");
      if (patientPageElement) {
        // Clear all input fields except the ones managed by React state (which will be cleared by setState above)
        const inputs = patientPageElement.querySelectorAll(
          'input:not([data-managed-state]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])',
        );
        inputs.forEach((input) => {
          if (
            !input.hasAttribute("data-managed-state") &&
            !input.value.includes("patientData.")
          ) {
            input.value = "";
          }
        });

        // Reset all select dropdowns to first option
        const selects = patientPageElement.querySelectorAll("select");
        selects.forEach((select) => {
          select.selectedIndex = 0;
        });

        // Clear all textarea fields
        const textareas = patientPageElement.querySelectorAll("textarea");
        textareas.forEach((textarea) => {
          textarea.value = "";
        });

        // Reset all checkboxes except consent (which is managed by state)
        const checkboxes = patientPageElement.querySelectorAll(
          'input[type="checkbox"]',
        );
        checkboxes.forEach((checkbox) => {
          checkbox.checked = false;
        });

        // Reset all radio buttons
        const radios = patientPageElement.querySelectorAll(
          'input[type="radio"]',
        );
        radios.forEach((radio) => {
          radio.checked = false;
        });
      }
    }, 50); // Small delay to ensure state updates are processed first

    // Navigate back to patients list page
    navigate("/patients");
  };

  // Function to collect all form data and register patient
  const handleRegister = async () => {
    if (!canWritePatient) {
      showError("You do not have permission to register or update patients.");
      return;
    }

    // Anti-tamper check: ensure no active errors are bypassed via force-trigger
    const activeErrors = Object.values(formFieldErrors).filter(Boolean);
    if (activeErrors.length > 0) {
      showError(activeErrors[0]);
      return;
    }

    try {
      // Top-notch comprehensive field validation
      const errors = {};
      const nameErr = validatePatientField(
        "fullName",
        patientData.fullName,
        true,
      );
      if (nameErr) errors.fullName = nameErr;

      const mobErr = validatePatientField(
        "mobileNumber",
        patientData.mobileNumber,
        true,
      );
      if (mobErr) errors.mobileNumber = mobErr;

      const dobErr = validatePatientField("dob", patientData.dob, true);
      if (dobErr) errors.dob = dobErr;

      const genErr = validatePatientField("gender", patientData.gender, true);
      if (genErr) errors.gender = genErr;

      const addrErr = validatePatientField("address", patientData.address, true);
      if (addrErr) errors.address = addrErr;

      if (patientData.pincode) {
        const pinErr = validatePatientField(
          "pincode",
          patientData.pincode,
          false,
        );
        if (pinErr) errors.pincode = pinErr;
      }

      if (patientData.email) {
        const emailErr = validatePatientField("email", patientData.email, false);
        if (emailErr) errors.email = emailErr;
      }

      if (Object.keys(errors).length > 0) {
        setFormFieldErrors(errors);
        showError(Object.values(errors)[0]);
        return;
      }

      // Mandatory consent verification
      if (!consentAgreed) {
        showError(
          "Please accept the consent statement before registering the patient.",
        );
        return;
      }

      // Set loading state only after validation passes
      setIsRegistering(true);

      const cleanFullName = patientData.fullName || patientData.name || "";
      const nameParts = cleanFullName.split(/\s+/).filter(Boolean);
      const f_name = nameParts[0] || "";
      const l_name =
        nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
      const m_name =
        nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";

      // Get Helper Data Names
      const selectedDoctorData =
        allDoctors.find((doc) => doc._id === selectedDoctor) ||
        doctors.find((doc) => doc._id === selectedDoctor);
      const selectedDepartmentData = departments.find(
        (dept) => dept._id === selectedDepartment,
      );
      const selectedReasonData = admissionReasons.find(
        (reason) => reason._id === selectedAdmissionReason,
      );
      const selectedInsuranceData = insuranceProviders.find(
        (prov) => prov._id === selectedInsuranceProvider,
      );

      const doctorName = selectedDoctorData
        ? selectedDoctorData.name ||
          `${selectedDoctorData.firstName || selectedDoctorData.f_name || ""} ${selectedDoctorData.lastName || selectedDoctorData.l_name || ""}`.trim() ||
          selectedDoctorData.email || ""
        : "";

      const deptName = selectedDepartmentData
        ? selectedDepartmentData.name
        : selectedDoctorData?.department?.name || "";

      const deptId =
        selectedDepartment ||
        selectedDoctorData?.department?._id ||
        selectedDoctorData?.departmentId ||
        (typeof selectedDoctorData?.department === "string"
          ? selectedDoctorData.department
          : "") ||
        "";

      const payload = {
        // --- Personal Info (Required) ---
        f_name: f_name,
        m_name: m_name,
        l_name: l_name,
        mobile: patientData.mobileNumber,

        age: patientData.age,
        ...(patientData.dob && { dob: patientData.dob }),

        gender: getGender(patientData.gender),
        email: patientData.email,
        bloodGroup: patientData.bloodGroup || "",
        aadhaarNumber: patientData.aadharNumber || "",
        abhaNumber: patientData.abhaNumber || "",
        abhaAddress: patientData.abhaAddress || "",

        // --- Address ---
        address: patientData.address,
        pincode: patientData.pincode,
        state: patientData.state,
        district: patientData.district,

        // --- Medical History (Optional Strings) ---
        allergies: patientData.allergies || "",
        existingMedicalConditions: patientData.existingMedicalConditions || "",
        ongoingMedications: patientData.ongoingMedications || "",

        // --- Visit Details (Triggers New Visit) ---
        consultingDoctor: doctorName,
        consultingDoctorId: selectedDoctor || "", // ID from dropdown object
        doctorId: selectedDoctor || "",

        department: deptName,
        departmentId: deptId,

        consultationFee: consultationFee ? Number(consultationFee) : 0,
        reasonForAdmission: selectedReasonData
          ? selectedReasonData.name || selectedReasonData.description || ""
          : patientData.reasonForAdmission || "", // Complaint

        // --- Payment ---
        paymentMode: selectedPaymentMode || "Cash",
        initialDepositAmount: initialDepositAmount
          ? Number(initialDepositAmount)
          : 0,

        // --- Insurance Details (Optional) ---
        insuranceProvider: selectedInsuranceData
          ? selectedInsuranceData.name
          : "",
        policyNumber: patientData.policyNumber || "",
        visitType: visitType || "General Visit",
        description: visitDescription || "",
        consent: consentAgreed,
        consentAgreed: consentAgreed,
      };

      // Call the new register API or update-and-visit if pre-filled
      const safePayload = deepSanitizePayload(payload);
      let response;
      if (selectedExistingPatient) {
        // Use PATCH for pre-filled patients
        response = await updatePatientAndVisit(
          selectedExistingPatient,
          safePayload,
        );
      } else {
        // Use POST for net-new patients
        response = await addNewPatient(safePayload);
      }

      console.log(
        selectedExistingPatient ? "Update Response:" : "Registration Response:",
        response,
      );

      // Check for success indicators:
      // 1. response.success is true
      // 2. response._id exists
      // 3. response.message implies success and no explicit error flag
      // 4. Just response existence implies success if axios didn't throw (depending on API style)
      const isSuccess =
        response &&
        (response.success === true ||
          response.success === "true" ||
          response._id ||
          (response.message && !response.error && !response.isError));

      if (isSuccess) {
        showSuccess(response.message || "Patient registered successfully!");

        // Navigate directly to patients list / OPD queue (skip print-invoice)
        sessionStorage.removeItem("invoiceData");
        setTimeout(() => {
          navigate("/patients");
        }, 800);
      } else {
        showError(response?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      showError(error.message || "Registration failed");
    } finally {
      setIsRegistering(false);
    }
  };

  const handleCheckExisting = async (checkParams) => {
    // build params from state if not directly passed
    const params = {
      mobile: checkParams?.mobileNumber || patientData.mobileNumber,
      abhaNumber:
        checkParams?.abhaNumber ||
        patientData.abhaNumber ||
        patientData.aadharNumber,
      abhaAddress: checkParams?.abhaAddress || patientData.abhaAddress,
    };

    // require at least one
    if (!params.mobile && !params.abhaNumber && !params.abhaAddress) {
      setExistingPatients([]);
      setShowExistingBanner(false);
      return;
    }

    // Only check if mobile is 10 digits when triggered by mobile
    if (
      params.mobile &&
      params.mobile.length !== 10 &&
      !params.abhaNumber &&
      !params.abhaAddress
    )
      return;

    try {
      setIsCheckingExisting(true);
      const response = await checkExistingPatient(params);
      if (response && response.data && response.data.length > 0) {
        setExistingPatients(response.data);
        setShowExistingBanner(true);
      } else {
        setExistingPatients([]);
        setShowExistingBanner(false);
      }
    } catch (error) {
      console.error("Error checking existing patient:", error);
      setExistingPatients([]);
      setShowExistingBanner(false);
    } finally {
      setIsCheckingExisting(false);
    }
  };

  const handlePreFill = (selectedProfile) => {
    // Use the same extraction logic as handleProfileSelected to be safe
    const getName = () => {
      if (selectedProfile.name && typeof selectedProfile.name === "string")
        return selectedProfile.name;
      if (selectedProfile.firstName && selectedProfile.lastName) {
        return `${selectedProfile.firstName} ${selectedProfile.middleName ? selectedProfile.middleName + ' ' : ''}${selectedProfile.lastName}`;
      }
      if (selectedProfile.firstName && typeof selectedProfile.firstName === "string")
        return selectedProfile.firstName;
      if (selectedProfile.f_name && typeof selectedProfile.f_name === "string") {
        return `${selectedProfile.f_name} ${selectedProfile.m_name ? selectedProfile.m_name + ' ' : ''}${selectedProfile.l_name}`;
      }
      return selectedProfile.fullName || "";
    };

    const getDob = () => {
      const dobRaw =
        selectedProfile.dob ||
        selectedProfile.dateOfBirth ||
        selectedProfile.date_of_birth;
      if (dobRaw) {
        let dobStr = String(dobRaw).split("T")[0];
        const dmyMatch = dobStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmyMatch) {
          return `${dmyMatch[3]}-${dmyMatch[2].padStart(2, "0")}-${dmyMatch[1].padStart(2, "0")}`;
        }
        const ymdMatch = dobStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
        if (ymdMatch) {
          return `${ymdMatch[1]}-${ymdMatch[2].padStart(2, "0")}-${ymdMatch[3].padStart(2, "0")}`;
        }
        return dobStr;
      }
      return "";
    };

    const getAge = () => {
      if (selectedProfile.age) return selectedProfile.age.toString();
      const dob = getDob();
      if (dob) {
        const birthDate = new Date(dob);
        const today = new Date();
        let calculatedAge = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          calculatedAge--;
        }
        return calculatedAge >= 0 ? calculatedAge.toString() : "";
      }
      return "";
    };

    const getGender = () => {
      const gender = selectedProfile.gender || selectedProfile.sex || "";
      if (gender === "M" || gender === "Male" || gender === "MALE")
        return "Male";
      if (gender === "F" || gender === "Female" || gender === "FEMALE")
        return "Female";
      if (gender === "O" || gender === "Other" || gender === "OTHER")
        return "Other";
      return "";
    };

    const prefillMobileRaw =
      selectedProfile.mobile ||
      selectedProfile.mobileNumber ||
      selectedProfile.phoneNumber ||
      selectedProfile.phone ||
      patientData.mobileNumber;

    const newPatientData = {
      fullName: getName(),
      age: getAge(),
      gender: getGender(),
      mobileNumber: prefillMobileRaw || "",
      aadharNumber:
        selectedProfile.aadhaarNumber ||
        selectedProfile.aadhar_number ||
        selectedProfile.aadharNumber ||
        patientData.aadharNumber,
      uhid: selectedProfile.uhid || "",
      address: selectedProfile.address || patientData.address,
      pincode: selectedProfile.pincode || patientData.pincode,
      state: selectedProfile.state || patientData.state,
      district: selectedProfile.district || patientData.district,
      email: selectedProfile.email || patientData.email,
      emergencyContact:
        selectedProfile.emergencyContact || patientData.emergencyContact,
      abhaAddress:
        selectedProfile.abhaaddress ||
        selectedProfile.abhaAddress ||
        patientData.abhaAddress,
      abhaNumber:
        selectedProfile.ABHANumber ||
        selectedProfile.abhaNumber ||
        patientData.abhaNumber,
      dob: getDob(),
      bloodGroup: selectedProfile.bloodGroup || patientData.bloodGroup,
      existingMedicalConditions:
        selectedProfile.existingMedicalConditions ||
        patientData.existingMedicalConditions,
      allergies: selectedProfile.allergies || patientData.allergies,
      ongoingMedications:
        selectedProfile.ongoingMedications || patientData.ongoingMedications,
    };

    // Reject any non-string/non-number field before it ever reaches form state
    const safePatientData = sanitizeProfileFields(newPatientData);

    setPatientData(safePatientData);
    setSelectedExistingPatient(selectedProfile._id || selectedProfile.uhid);
    setRegistrationMode("prefill");

    setShowPatientSelector(false);
    showSuccess(
      "Patient information auto-filled from existing profile! You can switch to Create New Profile if needed.",
    );
  };

  // Handle input changes for patient form with real-time feedback & validation
  const handleInputChange = (field, value) => {
    // Allow editing if ABHA is not linked OR if this specific field is not locked
    if (!isAbhaLinked || !lockedFields[field]) {
      const safeVal = typeof value === "string" ? value : String(value || "");
      setPatientData((prev) => ({
        ...prev,
        [field]: safeVal,
      }));

      // Real-time error feedback
      const isReq = [
        "fullName",
        "mobileNumber",
        "dob",
        "gender",
        "address",
      ].includes(field);
      const err = validatePatientField(field, safeVal, isReq);
      setFormFieldErrors((prev) => ({
        ...prev,
        [field]: err,
      }));
    }
  };

  const handleInputBlur = (field) => {
    const isReq = [
      "fullName",
      "mobileNumber",
      "dob",
      "gender",
      "address",
    ].includes(field);
    const err = validatePatientField(field, patientData[field], isReq);
    setFormFieldErrors((prev) => ({
      ...prev,
      [field]: err,
    }));
  };

  const isPrefillLocked = registrationMode === "prefill";
  const prefilledProfile = existingPatients.find(
    (p) => (p._id || p.uhid) === selectedExistingPatient,
  );
  const hasAddressData =
    prefilledProfile &&
    (prefilledProfile.address ||
      prefilledProfile.pincode ||
      prefilledProfile.state ||
      prefilledProfile.district);
  const isAddressLocked = isPrefillLocked && hasAddressData;

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="container-fluid">
        <div className="patient-page">
          <div className="py-3">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              {canWritePatient && (
                <button
                  className="customeBtn btnLightBlue"
                  onClick={() => navigate("/abha-home")}
                >
                  + Link ABHA
                </button>
              )}
            </div>
            {isAbhaLinked && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  backgroundColor: "#e8f5e8",
                  border: "1px solid #2e7d2e",
                  borderRadius: 5,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2e7d2e"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <strong>ABHA Profile Linked Successfully</strong>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "5px",
                    color: "#2e7d2e",
                  }}
                >
                  Patient details have been auto-filled from ABHA profile. You
                  can edit editable fields if needed.
                </div>
              </div>
            )}
            {isLoadingData && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  backgroundColor: "#f0f8ff",
                  border: "1px solid #0066cc",
                  borderRadius: 5,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      border: "2px solid #0066cc",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <strong>Loading form data...</strong>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "5px",
                    color: "#0066cc",
                  }}
                >
                  Please wait while we load doctors, departments, and other form
                  options.
                </div>
              </div>
            )}
          </div>
          {!canWritePatient && (
            <>
              <style>{`
                .allMainFeilds input, .allMainFeilds select, .allMainFeilds textarea {
                  background-color: #f8f9fa !important;
                  cursor: not-allowed !important;
                  color: #495057 !important;
                  pointer-events: none !important;
                }
                .allMainFeilds button:not(.read-only-allowed) {
                  pointer-events: none !important;
                  cursor: not-allowed !important;
                  opacity: 0.6 !important;
                }
              `}</style>
              <div
                className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                role="alert"
                style={{
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: "#EBF5FF",
                  color: "#1E429F",
                  border: "1px solid #B4C6FC",
                }}
              >
                <i className="material-icons" style={{ fontSize: "18px" }}>
                  visibility
                </i>
                <span>
                  <strong>Read-Only Mode:</strong> You do not have permission to
                  register or update patient records. All form fields are locked
                  for viewing only.
                </span>
              </div>
            </>
          )}
          <div className="allMainFeilds">
            <div className="basicDetails">
              <h4>Basic Details</h4>
              <div className="patientsFeildsRow">
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        Patient Name *{isAbhaLinked}
                      </label>
                      <input
                        type="text"
                        maxLength={50}
                        placeholder="Enter full name"
                        value={patientData.fullName}
                        onChange={(e) =>
                          handleInputChange("fullName", e.target.value)
                        }
                        onBlur={() => handleInputBlur("fullName")}
                        readOnly={
                          (isAbhaLinked && lockedFields.fullName) ||
                          (registrationMode === "prefill" &&
                            !!patientData.fullName)
                        }
                        data-managed-state="true"
                        className={formFieldErrors.fullName ? "is-invalid" : ""}
                        aria-invalid={
                          formFieldErrors.fullName ? "true" : "false"
                        }
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.fullName) ||
                            (registrationMode === "prefill" &&
                              !!patientData.fullName)
                              ? "#f5f5f5"
                              : "white",
                          border: formFieldErrors.fullName
                            ? "1.5px solid #EF4444"
                            : undefined,
                          cursor:
                            (isAbhaLinked && lockedFields.fullName) ||
                            (registrationMode === "prefill" &&
                              !!patientData.fullName)
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          (isAbhaLinked && lockedFields.fullName) ||
                          (registrationMode === "prefill" &&
                            !!patientData.fullName)
                            ? "Field auto-filled from profile"
                            : ""
                        }
                      />
                      {formFieldErrors.fullName && (
                        <div className="text-danger small mt-1">
                          {formFieldErrors.fullName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">DOB *{isAbhaLinked}</label>
                      <input
                        type="date"
                        value={patientData.dob || ""}
                        onBlur={() => handleInputBlur("dob")}
                        className={formFieldErrors.dob ? "is-invalid" : ""}
                        aria-invalid={formFieldErrors.dob ? "true" : "false"}
                        onChange={(e) => {
                          const newDob = e.target.value;
                          if (
                            (!isAbhaLinked || !lockedFields.age) &&
                            (registrationMode !== "prefill" || !patientData.dob)
                          ) {
                            setPatientData((prev) => {
                              const updates = { dob: newDob };
                              if (newDob) {
                                const birthDate = new Date(newDob);
                                const today = new Date();
                                let calculatedAge =
                                  today.getFullYear() - birthDate.getFullYear();
                                const m =
                                  today.getMonth() - birthDate.getMonth();
                                if (
                                  m < 0 ||
                                  (m === 0 &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  calculatedAge--;
                                }
                                updates.age =
                                  calculatedAge >= 0
                                    ? calculatedAge.toString()
                                    : "0";
                              } else {
                                updates.age = "";
                              }
                              return { ...prev, ...updates };
                            });
                          }
                        }}
                        readOnly={
                          (isAbhaLinked && lockedFields.age) ||
                          (registrationMode === "prefill" && !!patientData.dob)
                        }
                        data-managed-state="true"
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.age) ||
                            (registrationMode === "prefill" &&
                              !!patientData.dob)
                              ? "#f5f5f5"
                              : "white",
                          border: formFieldErrors.dob
                            ? "1.5px solid #EF4444"
                            : undefined,
                          cursor:
                            (isAbhaLinked && lockedFields.age) ||
                            (registrationMode === "prefill" &&
                              !!patientData.dob)
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          (isAbhaLinked && lockedFields.age) ||
                          (registrationMode === "prefill" && !!patientData.dob)
                            ? "Field auto-filled from profile"
                            : ""
                        }
                        max={new Date().toISOString().split("T")[0]}
                        required
                      />
                      {formFieldErrors.dob && (
                        <div className="text-danger small mt-1">
                          {formFieldErrors.dob}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Gender *{isAbhaLinked}</label>
                      <div className="radioGroup">
                        <label>
                          <input
                            type="radio"
                            name="gender"
                            checked={patientData.gender === "Female"}
                            onChange={(e) =>
                              handleInputChange("gender", "Female")
                            }
                            disabled={
                              (isAbhaLinked && lockedFields.gender) ||
                              (registrationMode === "prefill" &&
                                !!patientData.gender)
                            }
                            title={
                              (isAbhaLinked && lockedFields.gender) ||
                              (registrationMode === "prefill" &&
                                !!patientData.gender)
                                ? "Field auto-filled from profile"
                                : ""
                            }
                          />
                          <span>Female</span>
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="gender"
                            checked={patientData.gender === "Male"}
                            onChange={(e) =>
                              handleInputChange("gender", "Male")
                            }
                            disabled={
                              (isAbhaLinked && lockedFields.gender) ||
                              (registrationMode === "prefill" &&
                                !!patientData.gender)
                            }
                            title={
                              (isAbhaLinked && lockedFields.gender) ||
                              (registrationMode === "prefill" &&
                                !!patientData.gender)
                                ? "Field auto-filled from profile"
                                : ""
                            }
                          />
                          <span>Male</span>
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="gender"
                            checked={patientData.gender === "Other"}
                            onChange={(e) =>
                              handleInputChange("gender", "Other")
                            }
                            disabled={
                              (isAbhaLinked && lockedFields.gender) ||
                              (registrationMode === "prefill" &&
                                !!patientData.gender)
                            }
                            title={
                              (isAbhaLinked && lockedFields.gender) ||
                              (registrationMode === "prefill" &&
                                !!patientData.gender)
                                ? "Field auto-filled from profile"
                                : ""
                            }
                          />
                          <span>Other</span>
                        </label>
                      </div>
                      {formFieldErrors.gender && (
                        <div className="text-danger small mt-1">
                          {formFieldErrors.gender}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        Mobile Number *{isAbhaLinked}
                      </label>
                      <input
                        disabled={
                          (isAbhaLinked && lockedFields.mobileNumber) ||
                          (registrationMode === "prefill" &&
                            !!patientData.mobileNumber)
                        }
                        type="tel"
                        maxLength={10}
                        value={patientData.mobileNumber}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleInputChange("mobileNumber", val);
                          if (val.length === 10) {
                            handleCheckExisting({ mobileNumber: val });
                          } else if (showExistingBanner) {
                            setShowExistingBanner(false);
                            setExistingPatients([]);
                          }
                        }}
                        onBlur={() => {
                          handleCheckExisting();
                          handleInputBlur("mobileNumber");
                        }}
                        readOnly={
                          (isAbhaLinked && lockedFields.mobileNumber) ||
                          (registrationMode === "prefill" &&
                            !!patientData.mobileNumber)
                        }
                        data-managed-state="true"
                        className={
                          formFieldErrors.mobileNumber ? "is-invalid" : ""
                        }
                        aria-invalid={
                          formFieldErrors.mobileNumber ? "true" : "false"
                        }
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.mobileNumber) ||
                            (registrationMode === "prefill" &&
                              !!patientData.mobileNumber)
                              ? "#f5f5f5"
                              : "white",
                          border: formFieldErrors.mobileNumber
                            ? "1.5px solid #EF4444"
                            : undefined,
                          cursor:
                            (isAbhaLinked && lockedFields.mobileNumber) ||
                            (registrationMode === "prefill" &&
                              !!patientData.mobileNumber)
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          isAbhaLinked && lockedFields.mobileNumber
                            ? "Field auto-filled from ABHA profile"
                            : ""
                        }
                        placeholder={
                          isAbhaLinked &&
                          lockedFields.mobileNumber &&
                          patientData.mobileNumber
                            ? patientData.mobileNumber
                            : "Enter 10-digit mobile number"
                        }
                      />
                      {formFieldErrors.mobileNumber && (
                        <div className="text-danger small mt-1">
                          {formFieldErrors.mobileNumber}
                        </div>
                      )}
                      {isCheckingExisting && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "13px",
                              color: "#3b82f6",
                              fontWeight: "500",
                            }}
                          >
                            Checking for existing patient...
                          </span>
                        </div>
                      )}
                      {showExistingBanner && existingPatients.length > 0 && (
                        <span
                          style={{
                            color: "#3b82f6",
                            fontSize: "14px",
                            marginTop: "4px",
                            display: "inline-block",
                            cursor: "pointer",
                          }}
                        >
                          This Patients already exist
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        Aadhaar Number
                        {isAbhaLinked}
                      </label>
                      <input
                        type="text"
                        maxLength={12}
                        placeholder="Enter 12-digit Aadhaar number"
                        value={patientData.aadharNumber}
                        onChange={(e) =>
                          handleInputChange("aadharNumber", e.target.value)
                        }
                        readOnly={isAbhaLinked && lockedFields.aadharNumber}
                        data-managed-state="true"
                        style={{
                          backgroundColor:
                            isAbhaLinked && lockedFields.aadharNumber
                              ? "#f5f5f5"
                              : "white",
                          cursor:
                            isAbhaLinked && lockedFields.aadharNumber
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          isAbhaLinked && lockedFields.aadharNumber
                            ? "Field auto-filled from ABHA profile"
                            : ""
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons for Existing Patient */}
                {showExistingBanner && existingPatients.length > 0 && (
                  <div className="row mt-0 mb-2">
                    <div className="col-12 d-flex gap-2">
                      <button
                        type="button"
                        className={`customeBtn ${registrationMode === "prefill" ? "btnLightBlue" : "btnLightBlueBorder"}`}
                        onClick={() => {
                          if (existingPatients.length > 1) {
                            setShowPatientSelector(true);
                          } else {
                            handlePreFill(existingPatients[0]);
                          }
                        }}
                      >
                        Pre fill existing Info
                      </button>
                      <button
                        type="button"
                        className={`customeBtn ${registrationMode === "new" ? "btnLightBlue" : "btnLightBlueBorder"}`}
                        onClick={() => {
                          setRegistrationMode("new");
                          setSelectedExistingPatient(null);
                          setPatientData((prev) => ({
                            ...prev,
                            fullName: "",
                            age: "",
                            dob: "",
                            gender: "",
                            address: "",
                            pincode: "",
                            state: "",
                            district: "",
                            emergencyContact: "",
                            bloodGroup: "",
                            existingMedicalConditions: "",
                            allergies: "",
                            ongoingMedications: "",
                          }));
                        }}
                      >
                        Create New Patients
                      </button>
                    </div>
                  </div>
                )}
              </div>{" "}
            </div>
            <div className="basicDetails">
              <h4>Address Information</h4>
              <div className="patientsFeildsRow">
                <div className="row">
                  <div className="col-xs-12 col-md-6">
                    <div className="feilds">
                      <label className="label">
                        Address
                        {isAbhaLinked}
                      </label>
                      <textarea
                        value={patientData.address}
                        onChange={(e) =>
                          handleInputChange("address", e.target.value)
                        }
                        onBlur={() => handleInputBlur("address")}
                        readOnly={
                          (isAbhaLinked && lockedFields.address) ||
                          (registrationMode === "prefill" &&
                            !!patientData.address)
                        }
                        className={formFieldErrors.address ? "is-invalid" : ""}
                        aria-invalid={
                          formFieldErrors.address ? "true" : "false"
                        }
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.address) ||
                            (registrationMode === "prefill" &&
                              !!patientData.address)
                              ? "#f5f5f5"
                              : "white",
                          cursor:
                            (isAbhaLinked && lockedFields.address) ||
                            (registrationMode === "prefill" &&
                              !!patientData.address)
                              ? "not-allowed"
                              : "text",
                          width: "100%",
                          minHeight: "60px",
                          padding: "8px 12px",
                          border: formFieldErrors.address
                            ? "1.5px solid #EF4444"
                            : "1px solid #D1D5DB",
                          borderRadius: "8px",
                          resize: "vertical",
                        }}
                        title={
                          (isAbhaLinked && lockedFields.address) ||
                          (registrationMode === "prefill" &&
                            !!patientData.address)
                            ? "Field auto-filled from profile"
                            : ""
                        }
                        placeholder="Enter complete address"
                      />
                      {formFieldErrors.address && (
                        <div className="text-danger small mt-1">
                          {formFieldErrors.address}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        Pincode
                        {isAbhaLinked}
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="Enter 6-digit pincode"
                        value={patientData.pincode}
                        onChange={(e) =>
                          handleInputChange("pincode", e.target.value)
                        }
                        onBlur={() => handleInputBlur("pincode")}
                        readOnly={
                          (isAbhaLinked && lockedFields.pincode) ||
                          (registrationMode === "prefill" &&
                            !!patientData.pincode)
                        }
                        data-managed-state="true"
                        className={formFieldErrors.pincode ? "is-invalid" : ""}
                        aria-invalid={
                          formFieldErrors.pincode ? "true" : "false"
                        }
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.pincode) ||
                            (registrationMode === "prefill" &&
                              !!patientData.pincode)
                              ? "#f5f5f5"
                              : "white",
                          border: formFieldErrors.pincode
                            ? "1.5px solid #EF4444"
                            : undefined,
                          cursor:
                            (isAbhaLinked && lockedFields.pincode) ||
                            (registrationMode === "prefill" &&
                              !!patientData.pincode)
                              ? "not-allowed"
                              : "text",
                        }}
                      />
                      {formFieldErrors.pincode && (
                        <div className="text-danger small mt-1">
                          {formFieldErrors.pincode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        State
                        {isAbhaLinked}
                      </label>
                      <input
                        type="text"
                        value={patientData.state}
                        onChange={(e) =>
                          handleInputChange("state", e.target.value)
                        }
                        readOnly={
                          (isAbhaLinked && lockedFields.state) ||
                          (registrationMode === "prefill" &&
                            !!patientData.state)
                        }
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.state) ||
                            (registrationMode === "prefill" &&
                              !!patientData.state)
                              ? "#f5f5f5"
                              : "white",
                          cursor:
                            (isAbhaLinked && lockedFields.state) ||
                            (registrationMode === "prefill" &&
                              !!patientData.state)
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          (isAbhaLinked && lockedFields.state) ||
                          (registrationMode === "prefill" &&
                            !!patientData.state)
                            ? "Field auto-filled from profile"
                            : ""
                        }
                        placeholder="Enter state"
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        District
                        {isAbhaLinked}
                      </label>
                      <input
                        type="text"
                        value={patientData.district}
                        onChange={(e) =>
                          handleInputChange("district", e.target.value)
                        }
                        readOnly={
                          (isAbhaLinked && lockedFields.district) ||
                          (registrationMode === "prefill" &&
                            !!patientData.district)
                        }
                        style={{
                          backgroundColor:
                            (isAbhaLinked && lockedFields.district) ||
                            (registrationMode === "prefill" &&
                              !!patientData.district)
                              ? "#f5f5f5"
                              : "white",
                          cursor:
                            (isAbhaLinked && lockedFields.district) ||
                            (registrationMode === "prefill" &&
                              !!patientData.district)
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          (isAbhaLinked && lockedFields.district) ||
                          (registrationMode === "prefill" &&
                            !!patientData.district)
                            ? "Field auto-filled from profile"
                            : ""
                        }
                        placeholder="Enter district"
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">
                        Emergency Contact
                        {isAbhaLinked}
                      </label>
                      <input
                        type="tel"
                        maxLength={10}
                        value={patientData.emergencyContact}
                        onChange={(e) =>
                          handleInputChange("emergencyContact", e.target.value)
                        }
                        readOnly={
                          registrationMode === "prefill" &&
                          !!patientData.emergencyContact
                        }
                        placeholder="Enter emergency contact number"
                        style={{
                          backgroundColor:
                            registrationMode === "prefill" &&
                            !!patientData.emergencyContact
                              ? "#f5f5f5"
                              : "white",
                          cursor:
                            registrationMode === "prefill" &&
                            !!patientData.emergencyContact
                              ? "not-allowed"
                              : "text",
                        }}
                        title={
                          registrationMode === "prefill" &&
                          !!patientData.emergencyContact
                            ? "Field auto-filled from profile"
                            : ""
                        }
                        // placeholder="Enter emergency contact number"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="basicDetails ">
                <h4>Doctor & Department</h4>
                <div className="patientsFeildsRow">
                  <div className="row">
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Consulting Doctor *</label>
                        <select
                          value={selectedDoctor}
                          onChange={(e) => handleDoctorChange(e.target.value)}
                          disabled={isLoadingData && doctors.length === 0}
                        >
                          <option value="">
                            {isLoadingData && doctors.length === 0
                              ? "Loading doctors..."
                              : "Select Doctor"}
                          </option>
                          {doctors.map((doctor) => {
                            const docName = doctor.name
                              ? doctor.name.toLowerCase().startsWith("dr")
                                ? doctor.name
                                : `Dr. ${doctor.name}`
                              : `Dr. ${[
                                  doctor.firstName || doctor.f_name,
                                  doctor.lastName || doctor.l_name,
                                ]
                                  .filter(Boolean)
                                  .join(" ")}`.trim();
                            const docSpec =
                              doctor.specialization ||
                              doctor.department?.name ||
                              doctor.qualification ||
                              "";
                            return (
                              <option key={doctor._id} value={doctor._id}>
                                {docName}
                                {docSpec ? ` - ${docSpec}` : ""}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Department *</label>
                        <select
                          value={selectedDepartment}
                          onChange={(e) =>
                            handleDepartmentChange(e.target.value)
                          }
                          disabled={isLoadingData && departments.length === 0}
                        >
                          <option value="">
                            {isLoadingData && departments.length === 0
                              ? "Loading departments..."
                              : "Select Department"}
                          </option>
                          {departments.map((department) => (
                            <option key={department._id} value={department._id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Consultation Fee</label>
                        <input
                          type="text"
                          value={consultationFee}
                          onChange={(e) => setConsultationFee(e.target.value)}
                          placeholder="Fee will auto-populate when doctor is selected"
                        />
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Visit Type *</label>
                        <select
                          value={visitType}
                          onChange={(e) => setVisitType(e.target.value)}
                        >
                          <option value="General Visit">General Visit</option>
                          <option value="Antenatal">Antenatal</option>
                          <option value="Postnatal">Postnatal</option>
                          <option value="Day Surgery">Day Surgery</option>
                          <option value="Investigation Procedure">
                            Investigation Procedure
                          </option>
                          <option value="Mental Health">Mental Health</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Visit Description</label>
                        <input
                          type="text"
                          value={visitDescription}
                          onChange={(e) => setVisitDescription(e.target.value)}
                          placeholder="Additional notes for this visit"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="basicDetails ">
                <h4>Insurance Details</h4>
                <div className="patientsFeildsRow">
                  <div className="row">
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label"> Insurance Provider </label>
                        <select
                          value={selectedInsuranceProvider}
                          onChange={(e) =>
                            setSelectedInsuranceProvider(e.target.value)
                          }
                          disabled={
                            isLoadingData && insuranceProviders.length === 0
                          }
                        >
                          <option value="">Select Insurance Provider</option>
                          {insuranceProviders.map((provider) => (
                            <option key={provider._id} value={provider._id}>
                              {provider.name}{" "}
                              {provider.code ? `(${provider.code})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Policy Number</label>
                        <input
                          type="text"
                          placeholder="Enter policy number"
                          value={patientData.policyNumber}
                          onChange={(e) =>
                            handleInputChange("policyNumber", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="basicDetails ">
                <h4>Medical Detail</h4>
                <div className="patientsFeildsRow">
                  <div className="row">
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Reason for Admission </label>
                        <select
                          value={selectedAdmissionReason}
                          onChange={(e) =>
                            setSelectedAdmissionReason(e.target.value)
                          }
                          disabled={
                            isLoadingData && admissionReasons.length === 0
                          }
                        >
                          <option value="">Select Admission Reason</option>
                          {admissionReasons.map((reason) => (
                            <option key={reason._id} value={reason._id}>
                              {reason.description || reason.name}{" "}
                              {reason.code ? `(${reason.code})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">
                          Existing Medical Conditions
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Hypertension, Diabetes"
                          value={patientData.existingMedicalConditions}
                          onChange={(e) =>
                            handleInputChange(
                              "existingMedicalConditions",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Allergies</label>
                        <input
                          type="text"
                          placeholder="e.g., Penicillin, Pollen"
                          value={patientData.allergies}
                          onChange={(e) =>
                            handleInputChange("allergies", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Ongoing Medications</label>
                        <input
                          type="text"
                          placeholder="e.g., Metformin 500mg"
                          value={patientData.ongoingMedications}
                          onChange={(e) =>
                            handleInputChange(
                              "ongoingMedications",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="basicDetails ">
                <h4>Billing & Payment</h4>
                <div className="patientsFeildsRow">
                  <div className="row">
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Payment Mode</label>
                        <select
                          value={selectedPaymentMode}
                          onChange={(e) =>
                            setSelectedPaymentMode(e.target.value)
                          }
                        >
                          <option value="">Select Payment Mode</option>
                          <option value="online">Online</option>
                          <option value="cash">Cash</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Initial Deposit Amount</label>
                        <input
                          type="text"
                          placeholder="Enter deposit amount (₹)"
                          value={initialDepositAmount}
                          onChange={(e) =>
                            setInitialDepositAmount(e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="basicDetails ">
                <h4>Consent Forms</h4>
                <div className="agreeCheck">
                  <div className="row">
                    <div className="col-xs-12 col-md-12">
                      <div className="feilds">
                        <label
                          className="checkBoxCustome"
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          <h4>
                            I agree to terms and condition of Apollo Hopital
                            Digital Signature for Hospital Policies & Treatment
                            Authorization.
                          </h4>
                          {/* <p>Some helper text here</p> */}
                          <input
                            type="checkbox"
                            checked={consentAgreed}
                            onChange={(e) => setConsentAgreed(e.target.checked)}
                          />
                          <span className="checkmark" />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="basicDetailsAction">
                {!canWritePatient ? (
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <span
                      className="badge bg-secondary px-3 py-2"
                      style={{ fontSize: "13px" }}
                    >
                      Read-Only Mode ({user?.role_name || "Viewing"}) — Patient
                      registration is restricted
                    </span>
                    <button
                      type="button"
                      className="customeBtn btnEmpty read-only-allowed"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="customeBtn btnEmpty me-2"
                      onClick={handleClearAll}
                    >
                      Cancel & Go Back
                    </button>
                    <button
                      className="customeBtn btnLightBlue"
                      onClick={handleRegister}
                      disabled={
                        isRegistering ||
                        Object.values(formFieldErrors).some(Boolean)
                      }
                    >
                      {isRegistering ? "Registering..." : "Register"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Selector Modal */}
      {showPatientSelector && (
        <div
          className="customeSpinerLoader"
          style={{ zIndex: 1050, background: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="patient-selector-modal"
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h4
              style={{
                color: "#1f2a37",
                marginBottom: "16px",
                fontWeight: "600",
              }}
            >
              Select Existing Profile
            </h4>
            <p
              style={{
                color: "#6b7280",
                marginBottom: "20px",
                fontSize: "14px",
              }}
            >
              Multiple profiles matched the provided information. Please choose
              one to pre-fill.
            </p>
            <div
              className="patient-cards-list"
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {existingPatients.map((ep, idx) => (
                <div
                  key={idx}
                  className="patient-selector-card"
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "16px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#1c64f2";
                    e.currentTarget.style.backgroundColor = "#f0f8ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#d1d5db";
                    e.currentTarget.style.backgroundColor = "#fff";
                  }}
                  onClick={() => handlePreFill(ep)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "16px",
                        color: "#111928",
                      }}
                    >
                      {ep.name || ep.f_name + " " + (ep.l_name || "")}
                    </span>
                    <span style={{ fontSize: "14px", color: "#6b7280" }}>
                      {ep.uhid ? `UHID: ${ep.uhid}` : ""}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      fontSize: "14px",
                      color: "#4b5563",
                    }}
                  >
                    <span>
                      <strong>Gender:</strong> {ep.gender || "-"}
                    </span>
                    <span>
                      <strong>DOB:</strong>{" "}
                      {ep.dob ? ep.dob.split("T")[0] : "-"}
                    </span>
                  </div>
                  {ep.ABHANumber && (
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        fontSize: "14px",
                        color: "#4b5563",
                      }}
                    >
                      <span>
                        <strong>Abha Number:</strong> {ep.ABHANumber || "-"}
                      </span>
                      <span>
                        <strong>Abha Address:</strong> {ep.abhaaddress || "-"}
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      fontSize: "14px",
                      color: "#4b5563",
                      marginTop: "4px",
                    }}
                  >
                    <span>
                      <strong>Mobile:</strong> {ep.mobile || "-"}
                    </span>
                    <span>
                      <strong>Last Visit:</strong>{" "}
                      {ep.lastVisitDate &&
                      !isNaN(new Date(ep.lastVisitDate).getTime())
                        ? new Date(ep.lastVisitDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                className="customeBtn btnEmpty"
                onClick={() => setShowPatientSelector(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Patients;
